import { t } from "@lingui/core/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { Provider } from "jotai";
import { Suspense, useCallback, useEffect } from "react";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { FacilityLogo, useIsUserSignedIn } from "@/features/Session";
import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container";
import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { StoreStackNavigatorParamList } from "../../StoreStackNavigator";
import { StoreHomeDesktop } from "./StoreHomeDesktop";
import { StoreHomeMobile } from "./StoreHomeMobile";
import { StoreHomePageQuery } from "./__generated__/StoreHomePageQuery.graphql";

export const StoreHomePage = () => {
  const [queryRef, loadQuery] = useQueryLoader<StoreHomePageQuery>(
    StoreHomePageQueryString
  );
  useOpenQueriedStoreProduct();

  const doQuery = useCallback(() => {
    loadQuery({}, { fetchPolicy: "store-and-network" });
  }, [loadQuery]);

  useEffect(() => {
    doQuery();
  }, [doQuery]);

  const { top, bottom, left, right } = useSafeAreaInsets();

  return (
    <Box
      flex={1}
      bg="surface-primary"
      style={{
        paddingTop: top,
        paddingBottom: bottom,
        paddingLeft: left,
        paddingRight: right,
      }}
    >
      <Suspense fallback={null}>
        {queryRef ? (
          <StoreHomePageInner
            queryRef={queryRef}
            onPackageAuthComplete={doQuery}
          />
        ) : null}
      </Suspense>
    </Box>
  );
};

const useOpenQueriedStoreProduct = () => {
  const navigation =
    useNavigation<NavigationProp<StoreStackNavigatorParamList>>();
  const route =
    useRoute<RouteProp<StoreStackNavigatorParamList, "StoreHome">>();
  const membershipId = route.params?.membershipId;
  const packageId = route.params?.packageId;

  useEffect(() => {
    if (membershipId) {
      navigation.navigate("Memberships", { membershipId });
    } else if (packageId) {
      navigation.navigate("Packages", { packageId });
    }
  }, [membershipId, navigation, packageId]);
};

type InnerProps = {
  queryRef: PreloadedQuery<StoreHomePageQuery>;
  onPackageAuthComplete?: () => void;
};

const StoreHomePageInner = ({
  queryRef,
  onPackageAuthComplete,
}: InnerProps) => {
  const data = usePreloadedQuery(StoreHomePageQueryString, queryRef);

  const navigation =
    useNavigation<NavigationProp<StoreStackNavigatorParamList>>();

  const club = data.session?.currentClientProfile?.member?.club;
  const clubName = club?.name;

  const isSignedIn = useIsUserSignedIn(data.session);

  const canGoBack = navigation.canGoBack() && isSignedIn;

  const isMobile = useResponsiveProp({
    base: 1,
    lg: 0,
  });

  return (
    <Provider>
      <Container>
        <ScreenHeader
          leftItem={
            canGoBack ? (
              <Button
                accessibilityLabel={t`Back button`}
                variant="secondary"
                leftIcon={<ArrowLeftIcon />}
                onPress={() => {
                  navigation.goBack();
                }}
              />
            ) : (
              <FacilityLogo
                clubRef={club}
                imageProps={{
                  style: { height: 56, width: 56, borderRadius: 24 },
                }}
              />
            )
          }
          title={t`Store`}
          subHeading={clubName}
          includeHorizontalPadding={false}
        />
      </Container>
      <ScrollView>
        <Container>
          {isMobile ? (
            <StoreHomeMobile />
          ) : (
            <StoreHomeDesktop
              sessionRef={data.session}
              onPackageAuthComplete={onPackageAuthComplete}
            />
          )}
        </Container>
      </ScrollView>
    </Provider>
  );
};

const StoreHomePageQueryString = graphql`
  query StoreHomePageQuery {
    session {
      ...StoreHomeDesktop_session
      ...useIsUserSignedIn_session
      currentClientProfile {
        member {
          club {
            name
            ...FacilityLogoFragment_club
          }
        }
      }
    }
  }
`;
